import { Globe, Instagram } from "lucide-react";

import { CountdownCard } from "./CountdownCard";
import { LiveBadge } from "./LiveBadge";
import { SelecaoHeroForm } from "./SelecaoHeroForm";
import { formatSessionShort, getNextSession } from "@/lib/selecao-sessions";

const OFFICIAL_CHANNELS = [
  {
    href: "https://www.allugator.com/",
    label: "Site oficial Allu",
    icon: Globe,
  },
  {
    href: "https://www.instagram.com/allu.oficial/",
    label: "@allu.oficial",
    icon: Instagram,
  },
  {
    href: "https://allugatorinvest.com/",
    label: "Site Allu Invest",
    icon: Globe,
  },
  {
    href: "https://www.instagram.com/invistanaallu/",
    label: "@invistanaallu",
    icon: Instagram,
  },
];

export const SelecaoHero = () => {
  const next = getNextSession();
  const short = formatSessionShort(next.date);

  return (
    <section className="bg-[#FAF8F5]">
      <div className="container-wide pt-8 pb-8 sm:pt-12 sm:pb-10 md:pt-16 md:pb-12 lg:pt-20 lg:pb-14">
        {/* Grid mobile-first: stack natural; lg+ = split lateral 7/5 com bullets sob o texto */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-8 lg:gap-x-10 lg:gap-y-10 items-start">
          {/* Bloco 1 — texto + countdown (desktop: col esquerda) */}
          <div className="order-1 lg:col-span-7 lg:row-start-1">
            <LiveBadge>{`Encontro exclusivo · ${short}`}</LiveBadge>

            <h1
              className="mt-5 sm:mt-6 text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#112711] tracking-tight leading-[1.05]"
              style={{
                fontFamily:
                  '"Plus Jakarta Sans", Inter, system-ui, -apple-system, sans-serif',
                letterSpacing: "-0.03em",
              }}
            >
              Assessores estão deixando receita na mesa sem perceber.
            </h1>

            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-zinc-600 max-w-xl leading-relaxed">
              Descubra como assessores parceiros da Allu estão lucrando com uma
              oportunidade que a maioria do mercado ainda ignora, divulgando
              um produto exclusivo com 10 anos de histórico e rentabilidade
              acima da média.
            </p>

            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-zinc-700 max-w-xl leading-snug flex items-start gap-2.5">
              <span
                aria-hidden="true"
                className="mt-2 sm:mt-2.5 block h-1.5 w-1.5 rounded-full bg-[#4BD084] shrink-0"
              />
              <span>
                Acompanhe a live e entenda como{" "}
                <span className="font-bold text-[#112711]">
                  aumentar sua receita como assessor
                </span>{" "}
                sendo parceiro Allu Invest.
              </span>
            </p>

            <CountdownCard target={next.date} className="mt-6 sm:mt-7 max-w-md" />
          </div>

          {/* Bloco 2 — Form (mobile: vem após texto; desktop: col direita spanning 2 rows) */}
          <div className="order-2 lg:col-span-5 lg:row-start-1 lg:row-span-2 w-full">
            <SelecaoHeroForm />
          </div>

          {/* Bloco 3 — bullets (mobile: após o form; desktop: sob texto na col esquerda) */}
          <div className="order-3 lg:col-span-7 lg:col-start-1 lg:row-start-2">
            <ul className="space-y-2.5 text-sm sm:text-[15px] text-zinc-700">
              <li className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-2 block w-1 h-1 rounded-full bg-[#112711] shrink-0"
                />
                <span>
                  Sessão de 60 minutos · entenda 100% como funciona · sem replay
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-2 block w-1 h-1 rounded-full bg-[#112711] shrink-0"
                />
                <span>
                  Programa exclusivo para profissionais certificados
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-2 block w-1 h-1 rounded-full bg-[#112711] shrink-0"
                />
                <span>
                  Aplicação revisada manualmente — nem todo cadastro é aprovado
                </span>
              </li>
            </ul>
          </div>

          {/* Bloco 4 — Canais oficiais + KPMG badge (rodapé sutil da 1ª dobra) */}
          <div className="order-4 lg:col-span-12 lg:col-start-1 lg:row-start-3 mt-1 lg:mt-3 pt-3 sm:pt-4 border-t border-zinc-200/80">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              {/* Canais oficiais */}
              <div>
                <div className="text-[9px] font-semibold tracking-[0.14em] uppercase text-zinc-400 mb-1.5">
                  Canais oficiais
                </div>
                <ul className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] sm:text-xs">
                  {OFFICIAL_CHANNELS.map((c) => (
                    <li key={c.href}>
                      <a
                        href={c.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#112711] transition-colors"
                      >
                        <c.icon className="w-3 h-3" aria-hidden="true" />
                        {c.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Badge KPMG Emerging Giants */}
              <div className="inline-flex items-center gap-2.5 self-start sm:self-end">
                <img
                  src="/selecao/kpmg.svg"
                  alt="KPMG"
                  className="h-8 sm:h-10 w-auto"
                  loading="lazy"
                  decoding="async"
                />
                <div className="leading-tight">
                  <div className="text-[9px] font-semibold tracking-[0.14em] uppercase text-zinc-400">
                    Reconhecida pelo
                  </div>
                  <div className="text-[11px] sm:text-xs font-semibold text-[#112711]">
                    KPMG Emerging Giants 2026
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
